<template>
	<v-data-table
		data-tour="table"
		:headers="headers"
		:items="items"
		:options="options"
		:server-items-length="total"
		:loading="loading"
		class="elevation-0"
		:footer-props="{ 'items-per-page-options': [25, 50, 100] }"
		item-key="_id"
		:hide-default-header="mobileCards"
		:class="{ 'table-cards': mobileCards }"
		@update:options="$emit('update:options', $event)"
	>
		<!-- telefono: ogni riga diventa una card chiave/valore -->
		<template v-if="mobileCards" v-slot:body="{ items: righe }">
			<tbody class="price-cards">
				<tr v-if="righe.length === 0">
					<td :colspan="headers.length" class="px-3 py-1">
						<div class="grey--text text-center py-4">{{ loading ? 'Caricamento...' : 'Nessun elemento' }}</div>
					</td>
				</tr>
				<tr v-for="item in righe" :key="item._id">
					<td :colspan="headers.length" class="px-3 py-1">
						<key-value-card :title="item.title || item.sellerSku || '-'" :fields="campiCard(item)" :image="item.image || ''">
							<template v-slot:top>
								<div class="d-flex align-center">
									<v-btn icon small data-tour="follow" :loading="followingId === item._id" @click="$emit('follow', item)">
										<v-icon :color="item.followed ? 'amber' : 'grey'">{{ item.followed ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
									</v-btn>
									<v-chip x-small :color="item._item ? 'primary' : 'grey'" text-color="white" class="ml-1">{{ item._item ? 'Associato' : 'Non associato' }}</v-chip>
									<v-chip v-if="!item.ean" x-small color="grey lighten-1" class="ml-1">Senza EAN</v-chip>
								</div>
							</template>
							<div class="d-flex justify-space-between">
								<span class="card-label">Confronto</span>
								<v-chip x-small :color="compareChip(item).color" text-color="white">{{ compareChip(item).text }}</v-chip>
							</div>
							<template v-slot:actions>
								<v-btn small text color="primary" data-tour="analyze" :loading="analyzingId === item._id" :disabled="analyzeDisabled || !item.ean" :title="item.ean ? 'Cerca subito gli altri venditori con lo stesso EAN' : 'Manca l’EAN: su eBay non si può confrontare'" @click="$emit('analyze', item)">
									<v-icon left small>mdi-chart-line</v-icon>
									Analizza ora
								</v-btn>
								<v-btn small color="primary" text data-tour="adjust" :disabled="!canAdjust(item)" @click="$emit('adjust', item)">
									<v-icon left small>mdi-tune-variant</v-icon>
									Aggiusta prezzo
								</v-btn>
							</template>
						</key-value-card>
					</td>
				</tr>
			</tbody>
		</template>

		<template v-slot:item.followed="{ item }">
			<v-btn icon data-tour="follow" :loading="followingId === item._id" @click="$emit('follow', item)">
				<v-icon :color="item.followed ? 'amber' : 'grey'">{{ item.followed ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
			</v-btn>
		</template>
		<template v-slot:item.image="{ item }">
			<v-img v-if="item.image" :src="item.image" max-height="48" max-width="48" contain class="my-1"></v-img>
			<v-icon v-else color="grey lighten-1">mdi-image-off-outline</v-icon>
		</template>
		<template v-slot:item.title="{ item }">
			<span v-if="item.title">{{ item.title }}</span>
			<span v-else class="grey--text">—</span>
			<div>
				<v-chip x-small :color="item._item ? 'primary' : 'grey'" text-color="white">{{ item._item ? 'Associato' : 'Non associato' }}</v-chip>
				<v-chip v-if="item.active === false" x-small color="warning" class="ml-1">Non più attiva</v-chip>
				<span v-if="item.ean" class="caption grey--text ml-1">EAN {{ item.ean }}</span>
				<v-chip v-else x-small color="grey lighten-1" class="ml-1">Senza EAN: non confrontabile</v-chip>
			</div>
		</template>
		<template v-slot:item.sellerSku="{ item }">
			<div>{{ item.sellerSku }}</div>
			<a v-if="listingUrl(item)" :href="listingUrl(item)" target="_blank" rel="noopener" class="caption">{{ item.itemId }}</a>
			<span v-else class="caption">{{ item.itemId || '-' }}</span>
		</template>
		<template v-slot:item.price="{ item }">
			<span v-if="item.price === null || item.price === undefined">-</span>
			<template v-else>
				<span>{{ money(item.price, item.currency) }}</span>
				<!-- il cliente paga prezzo + spedizione: e' il totale che va confrontato -->
				<div v-if="ourTotalOf(item) !== null" class="caption" :class="isBeaten(item) ? 'red--text font-weight-bold' : 'grey--text'">
					+ {{ money(ourShippingOf(item), item.currency) }} sped. = {{ money(ourTotalOf(item), item.currency) }}
				</div>
			</template>
			<div v-if="item.lastAdjust && item.lastAdjust.at" class="caption grey--text">aggiustato il {{ item.lastAdjust.at | moment('DD/MM HH:mm') }}</div>
		</template>
		<template v-slot:item.competitor="{ item }">
			<v-chip small :color="compareChip(item).color" text-color="white">{{ compareChip(item).text }}</v-chip>
			<template v-if="item.analysis && item.analysis.competitorTotal !== null && item.analysis.competitorTotal !== undefined">
				<div class="font-weight-bold mt-1">
					<a v-if="competitorUrl(item)" :href="competitorUrl(item)" target="_blank" rel="noopener">{{ money(item.analysis.competitorTotal, item.analysis.currency) }}</a>
					<span v-else>{{ money(item.analysis.competitorTotal, item.analysis.currency) }}</span>
				</div>
				<div class="caption grey--text">
					{{ money(item.analysis.competitorPrice, item.analysis.currency) }} + {{ money(item.analysis.competitorShipping || 0, item.analysis.currency) }} sped.
					<span v-if="item.analysis.competitorSeller"> — {{ item.analysis.competitorSeller }}</span>
				</div>
			</template>
			<div v-if="item.analysis && ['error', 'skipped'].includes(item.analysis.status)" class="caption red--text text-truncate" style="max-width: 220px" :title="item.analysis.error">
				{{ item.analysis.error }}
			</div>
		</template>
		<template v-slot:item.position="{ item }">
			<span v-if="item.analysis && item.analysis.position">{{ item.analysis.position }}° su {{ item.analysis.offers }}</span>
			<span v-else-if="item.analysis && item.analysis.status === 'done' && !item.analysis.ourFound" class="caption grey--text">non trovata</span>
			<span v-else>-</span>
		</template>
		<template v-slot:item.analysisAt="{ item }">
			<span v-if="item.analysis && item.analysis.searchedAt">{{ item.analysis.searchedAt | moment('DD/MM/YYYY HH:mm') }}</span>
			<span v-else>-</span>
		</template>
		<template v-slot:item.actions="{ item }">
			<v-btn small text color="primary" data-tour="analyze" :loading="analyzingId === item._id" :disabled="analyzeDisabled || !item.ean" :title="item.ean ? 'Cerca subito gli altri venditori con lo stesso EAN' : 'Manca l’EAN: su eBay non si può confrontare'" @click="$emit('analyze', item)">
				<v-icon left small>mdi-chart-line</v-icon>
				Analizza ora
			</v-btn>
			<v-btn small color="primary" text data-tour="adjust" :disabled="!canAdjust(item)" @click="$emit('adjust', item)">
				<v-icon left small>mdi-tune-variant</v-icon>
				Aggiusta prezzo
			</v-btn>
		</template>
	</v-data-table>
</template>

<script>
import KeyValueCard from '@/components/core/KeyValueCard.vue';
import { regions } from '@/utils/ebay/marketplace';
import { ourShipping, ourTotal, isBeaten } from './pricing';

// Tabella di "Analisi prezzi" eBay: solo presentazione, le azioni salgono al contenitore.
export default {
	name: 'EbayPriceTable',
	components: { KeyValueCard },
	props: {
		items: { type: Array, default: () => [] },
		total: { type: Number, default: 0 },
		options: { type: Object, required: true },
		loading: { type: Boolean, default: false },
		mobileCards: { type: Boolean, default: false },
		followingId: { type: String, default: '' },
		// riga su cui gira "Analizza ora" e blocco mentre gira l'analisi completa (quota condivisa)
		analyzingId: { type: String, default: '' },
		analyzeDisabled: { type: Boolean, default: false },
	},
	data() {
		return {
			headers: [
				{ text: '', value: 'followed', sortable: false, width: 56 },
				{ text: '', value: 'image', sortable: false, width: 70 },
				{ text: 'Titolo', value: 'title', sortable: false },
				{ text: 'Seller Sku / ItemID', value: 'sellerSku', sortable: false },
				{ text: 'Nostro prezzo', value: 'price', sortable: false },
				{ text: 'Concorrente più basso', value: 'competitor', sortable: false },
				{ text: 'Posizione', value: 'position', sortable: false },
				{ text: 'Ricerca', value: 'analysisAt', sortable: false },
				{ text: '', value: 'actions', sortable: false, align: 'end' },
			],
		};
	},
	methods: {
		ourShippingOf: ourShipping,
		ourTotalOf: ourTotal,
		isBeaten,
		money(value, currency) {
			if (value === null || value === undefined) {
				return '-';
			}
			return `${Number(value).toFixed(2)} ${currency || ''}`.trim();
		},
		// si aggiusta solo con un concorrente trovato
		canAdjust(item) {
			const analysis = item.analysis || {};
			return analysis.status === 'done' && analysis.competitorTotal !== null && analysis.competitorTotal !== undefined;
		},
		compareChip(item) {
			const analysis = item.analysis || {};
			if (!item.ean && !analysis.status) {
				return { text: 'Senza EAN', color: 'grey lighten-1' };
			}
			switch (analysis.status) {
				case 'done':
					if (!analysis.ourFound) {
						return { text: 'Concorrenti trovati', color: 'info' };
					}
					return isBeaten(item) || analysis.isCheapest === false ? { text: 'Più caro', color: 'error' } : { text: 'Il più economico', color: 'success' };
				case 'no_competitors':
					return { text: 'Nessun concorrente', color: 'grey darken-1' };
				case 'queued':
					return { text: 'In coda', color: 'info' };
				case 'skipped':
					return { text: 'Da rifare', color: 'warning' };
				case 'error':
					return { text: 'Errore', color: 'error' };
				default:
					return item.followed ? { text: 'Da cercare', color: 'grey' } : { text: 'Non seguito', color: 'grey lighten-1' };
			}
		},
		regionUrl(item) {
			const region = regions.find(r => r.MarketplaceId === item.marketplaceId);
			return region && region.url ? region.url.replace('http://', 'https://') : '';
		},
		listingUrl(item) {
			const base = this.regionUrl(item);
			return base && item.itemId ? `${base}itm/${item.itemId}` : '';
		},
		competitorUrl(item) {
			const base = this.regionUrl(item);
			const id = item.analysis && item.analysis.competitorItemId;
			return base && id ? `${base}itm/${id}` : '';
		},
		campiCard(item) {
			const total = ourTotal(item);
			const analysis = item.analysis || {};
			return [
				{ label: 'Seller Sku', value: item.sellerSku, strong: true },
				{ label: 'EAN', value: item.ean || '-' },
				{
					label: 'Nostro prezzo',
					value: total !== null ? `${this.money(item.price, item.currency)} + ${this.money(ourShipping(item), item.currency)} sped. = ${this.money(total, item.currency)}` : this.money(item.price, item.currency),
					strong: isBeaten(item),
				},
				{ label: 'Concorrente', value: this.money(analysis.competitorTotal, analysis.currency) },
				{ label: 'Posizione', value: analysis.position ? `${analysis.position}° su ${analysis.offers}` : '-' },
			];
		},
	},
};
</script>

<style scoped>
.table-cards ::v-deep table {
	display: block;
	width: 100%;
}
.price-cards,
.price-cards tr {
	display: block;
	width: 100%;
}
.price-cards tr,
.price-cards tr:hover {
	background: transparent !important;
}
.price-cards td {
	display: block;
	width: 100%;
	border-bottom: none !important;
	height: auto !important;
}
.price-cards tr:first-child td {
	padding-top: 12px !important;
}
.price-cards tr:last-child td {
	padding-bottom: 12px !important;
}
.price-cards tr + tr td {
	padding-top: 8px !important;
}
.card-label {
	font-size: 14px;
	color: rgba(0, 0, 0, 0.6);
}
</style>
